import {useState, useEffect, useRef} from 'react';
import {CtaButton} from '../cta-button/cta-button';
import ProfessionalVideoPlayer from '../video-player/professional-video-player';

// Dữ liệu đã được cập nhật với Video ID mới
const bannerData = {
    title: 'Vươn Concert',
    description: `Trong tháng 10 này, <strong>KidsPlaza</strong> trân trọng gửi tới quý khách hàng chương trình âm nhạc đặc biệt <strong>"Vươn Concert"</strong> trong chuỗi sự kiện <strong>Festival mẹ bầu và em bé 2025</strong>. Đây sẽ là một đêm nhạc đầy cảm xúc - nơi âm nhạc, tình yêu thương và khát vọng tương lai hòa quyện. Không chỉ dừng lại ở một đêm nhạc, <strong>"Vươn Concert"</strong> còn là thông điệp về sự đồng hành: <strong>vươn cao - vươn xa cùng con</strong>.`,
    buttonText: 'Đăng ký nhận vé',
};

// Video URL - có thể là MP4, HLS (.m3u8), hoặc DASH (.mpd)
const videoUrl =
    'https://static.kidsplaza.vn/assets/festival2025/vuon-concert-2025/videos/vuon-concert-video-demo-02.mp4';
const posterImageUrl =
    'https://static.kidsplaza.vn/assets/festival2025/vuon-concert-2025/images/optimized/concert-poster.webp';

const ConcertBanner = () => {
    const [hideFloatingButton, setHideFloatingButton] = useState(false);
    const [isMuted, setIsMuted] = useState(true);
    const playerRef = useRef<any>(null);

    const toggleMute = () => {
        if (playerRef.current) {
            const currentMuted = playerRef.current.muted();
            playerRef.current.muted(!currentMuted);
            setIsMuted(!currentMuted);
        }
    };

    const handlePlayerReady = (player: any) => {
        playerRef.current = player;
        console.log('Professional video player is ready');

        // Set initial muted state
        player.muted(isMuted);

        // Custom event listeners - chỉ update state, không trigger play
        player.on('volumechange', () => {
            const newMutedState = player.muted();
            if (newMutedState !== isMuted) {
                setIsMuted(newMutedState);
            }
        });
    };

    const handlePlayerError = (error: any) => {
        console.error('Video player error:', error);
    };

    const scrollToRegisterSection = () => {
        const registerSection = document.getElementById('register-section');
        if (registerSection) {
            registerSection.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
                inline: 'nearest',
            });

            // Fallback for browsers that don't support smooth scrolling well
            // hoặc custom scroll animation for more control
            if (CSS.supports('scroll-behavior', 'smooth')) {
                // Browser supports smooth scroll natively
                return;
            } else {
                // Custom smooth scroll for better control
                const targetPosition = registerSection.offsetTop;
                const startPosition = window.pageYOffset;
                const distance = targetPosition - startPosition;
                const duration = 1200; // 1.2 seconds for smoother animation
                let start: number | null = null;

                const smoothScrollStep = (timestamp: number) => {
                    if (!start) start = timestamp;
                    const progress = timestamp - start;
                    const percentage = Math.min(progress / duration, 1);

                    // Easing function for smoother animation
                    const ease = 0.5 * (1 - Math.cos(percentage * Math.PI));

                    window.scrollTo(0, startPosition + distance * ease);

                    if (progress < duration) {
                        window.requestAnimationFrame(smoothScrollStep);
                    }
                };

                window.requestAnimationFrame(smoothScrollStep);
            }
        }
    };

    useEffect(() => {
        const handleScroll = () => {
            const registerSection = document.getElementById('register-section');
            if (registerSection) {
                const sectionTop = registerSection.getBoundingClientRect().top;
                const windowHeight = window.innerHeight;

                // Ẩn button khi register section xuất hiện trong viewport
                // Threshold: khi 30% của section đã visible
                if (sectionTop <= windowHeight * 0.7) {
                    setHideFloatingButton(true);
                } else {
                    setHideFloatingButton(false);
                }
            }
        };

        window.addEventListener('scroll', handleScroll);
        handleScroll(); // Check initial state

        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    return (
        <div className="mx-auto bg-gray-100 w-full max-w-[2000px] overflow-x-hidden">
            {/* Main Content */}
            <main className="shadow-xl overflow-y-hidden">
                {/* 1. Professional Video Player with Adaptive Streaming */}
                <div className="relative w-full pt-[56.25%] bg-gray-200">
                    <div className="absolute inset-0">
                        <ProfessionalVideoPlayer
                            videoUrl={videoUrl}
                            posterUrl={posterImageUrl}
                            autoplay={true}
                            muted={isMuted}
                            loop={true}
                            className="w-full h-full object-cover"
                            onReady={handlePlayerReady}
                            onError={handlePlayerError}
                        />
                    </div>

                    {/* Custom Mute/Unmute Toggle Button */}
                    <button
                        aria-label="Điều chỉnh âm thanh"
                        onClick={toggleMute}
                        className="absolute top-4 right-4 z-30 bg-black/50 hover:bg-black/70 text-white p-3 rounded-full transition-all duration-200 backdrop-blur-sm"
                        style={{
                            WebkitBackdropFilter: 'blur(4px)',
                        }}
                        title={isMuted ? 'Bật âm thanh' : 'Tắt âm thanh'}>
                        {isMuted ? (
                            // Muted Icon (Volume Off)
                            <svg
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                                className="w-5 h-5">
                                <path
                                    d="M16.5 12C16.5 10.23 15.5 8.71 14 7.97V10.18L16.45 12.63C16.48 12.43 16.5 12.22 16.5 12ZM19 12C19 12.94 18.8 13.82 18.46 14.64L19.97 16.15C20.63 14.91 21 13.5 21 12C21 7.72 18 4.14 14 3.23V5.29C16.89 6.15 19 8.83 19 12ZM4.27 3L3 4.27L7.73 9H3V15H7L12 20V13.27L16.25 17.53C15.58 18.04 14.83 18.46 14 18.7V20.77C15.38 20.45 16.63 19.82 17.68 18.96L19.73 21L21 19.73L12 10.73L4.27 3ZM12 4L9.91 6.09L12 8.18V4Z"
                                    fill="currentColor"
                                />
                            </svg>
                        ) : (
                            // Unmuted Icon (Volume On)
                            <svg
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                                className="w-5 h-5">
                                <path
                                    d="M3 9V15H7L12 20V4L7 9H3ZM16.5 12C16.5 10.23 15.5 8.71 14 7.97V16.02C15.5 15.29 16.5 13.77 16.5 12ZM14 3.23V5.29C16.89 6.15 19 8.83 19 12C19 15.17 16.89 17.85 14 18.71V20.77C18 19.86 21 16.28 21 12C21 7.72 18 4.14 14 3.23Z"
                                    fill="currentColor"
                                />
                            </svg>
                        )}
                    </button>

                    {/* Info section overlay for desktop only - từ laptop trở lên */}
                    <section
                        className="hidden lg:block absolute bottom-0 left-0 right-0 text-white p-9 backdrop-blur-[5.5px] z-10"
                        style={{
                            background:
                                'linear-gradient(97.14deg,rgba(0,62,222,0.576) 18.79%,rgba(197,49,255,0.6) 59.23%,rgba(77,57,255,0.6) 76.88%);',
                        }}>
                        {/* Background overlay layer - chỉ trong section */}
                        <div
                            className="absolute inset-0 z-0"
                            style={{
                                background: '#0011d6',
                                opacity: 0.1,
                            }}
                        />

                        {/* Button for desktop only */}
                        <div className="hidden lg:flex absolute right-6 lg:right-12 -top-6 lg:top-[-1.5rem] justify-end z-20">
                            {/* <CtaButton */}
                            {/*   text={<p className={'uppercase'}>{bannerData.buttonText}</p>} */}
                            {/*   className={'mx-auto'} */}
                            {/*   contentClassName='font-next_roboto font-bold text-[16px] lg:text-[18px] px-3 py-1' */}
                            {/*   onClick={scrollToRegisterSection} */}
                            {/* /> */}
                            {/*<button*/}
                            {/*  className="py-3 px-7 rounded-full bg-[linear-gradient(103.28deg,rgba(230,255,253,0.98)_1.06%,rgba(246,233,137,0.98)_95.93%)] shadow-md transition transform hover:scale-105 focus:outline-none focus:ring-4 focus:ring-yellow-200">*/}
                            {/*  <LinearText className="font-bold text-lg">{bannerData.buttonText}</LinearText>*/}
                            {/*</button>*/}
                        </div>
                        <div className="flex flex-col lg:flex-row items-center gap-6 lg:gap-12 xl:!gap-[112px] max-w-[1200px] mx-auto text-center lg:text-left relative z-10">
                            <div className="w-fit">
                                <h1 className="text-3xl lg:text-5xl font-bold mb-4 lg:mb-0 w-fit">
                                    {bannerData.title}
                                </h1>
                            </div>
                            <div className="w-full md:!flex-1">
                                <p
                                    className="text-base lg:text-lg leading-relaxed text-justify"
                                    dangerouslySetInnerHTML={{__html: bannerData.description}}
                                />
                            </div>
                        </div>
                    </section>
                </div>
                {/* Info section for mobile/tablet only (below video) - từ iPad trở xuống */}
                <section
                    className="block lg:hidden text-white p-2 relative"
                    style={{
                        background:
                            'linear-gradient(97.14deg,rgba(0,62,222,0.576) 18.79%,rgba(197,49,255,0.6) 59.23%,rgba(77,57,255,0.6) 76.88%);',
                    }}>
                    {/* Background overlay layer for mobile - chỉ trong section */}
                    <div
                        className="absolute inset-0 z-0"
                        style={{
                            background: '#0011d6',
                            opacity: 0.8,
                        }}
                    />

                    <div className="flex flex-col items-center max-w-[1200px] mx-auto text-left relative z-10">
                        <div className="w-full text-center">
                            <h1 className="text-[28px] font-bold">{bannerData.title}</h1>
                        </div>
                        <div className="w-full">
                            <p
                                className="text-[12px] leading-relaxed"
                                dangerouslySetInnerHTML={{__html: bannerData.description}}
                            />
                        </div>
                    </div>
                </section>
                {/* Sticky button for mobile/tablet only - với animation ẩn/hiện */}
                <div className="lg:hidden">
                    <div
                        className={`fixed bottom-4 z-[100] py-3 w-full ${
                            hideFloatingButton
                                ? 'opacity-0 translate-y-4 pointer-events-none'
                                : 'opacity-100 translate-y-0 pointer-events-auto'
                        }`}>
                        <CtaButton
                            text={
                                <>
                                    <p className={'uppercase text-[18px]'}>{bannerData.buttonText}</p>
                                    <p className="text-[16px]">(Miễn phí)</p>
                                </>
                            }
                            className={'mx-auto w-[20%] min-w-[291px] max-w-[665px]'}
                            contentClassName="font-next_roboto font-bold text-[16px] lg:text-[36px]"
                            onClick={scrollToRegisterSection}
                        />
                    </div>
                </div>

                {/* Sticky button for DESKTOP only - thêm tối thiểu, tái dùng hideFloatingButton */}
                <div className="hidden lg:block">
                    <div
                        className={`fixed bottom-8 right-10 z-[999] transition-all duration-200 ${
                            hideFloatingButton
                                ? 'opacity-0 translate-y-3 pointer-events-none'
                                : 'opacity-100 translate-y-0 pointer-events-auto'
                        }`}>
                        <CtaButton
                            text={
                                <>
                                    <p className={'uppercase text-[18px]'}>{bannerData.buttonText}</p>
                                    <p className="text-[16px]">(Miễn phí)</p>
                                </>
                            }
                            className="mx-auto"
                            contentClassName="font-next_roboto font-bold text-[18px] px-4 py-2"
                            onClick={scrollToRegisterSection}
                        />
                    </div>
                </div>
            </main>
        </div>
    );
};
export default ConcertBanner;
